import React from 'react';

const COLORS = ['#6366f1','#22c55e','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6','#f97316','#06b6d4','#84cc16','#a855f7','#e11d48'];

function ChartsComponent({ vehicle }) {
  if (!vehicle) return null;

  const history = vehicle.history || [];

  // Component breakdown
  const componentCounts = {};
  history.forEach(h => {
    const comp = (h.components || 'Unknown').split(',')[0].trim();
    componentCounts[comp] = (componentCounts[comp] || 0) + 1;
  });
  const sortedComponents = Object.entries(componentCounts).sort((a, b) => b[1] - a[1]);
  const total = sortedComponents.reduce((s, c) => s + c[1], 0);

  // Complaints over time (group by year)
  const yearCounts = {};
  history.forEach(h => {
    const date = h.dateOfIncident || h.dateComplaintFiled;
    if (date) {
      const year = date.substring(0, 4);
      yearCounts[year] = (yearCounts[year] || 0) + 1;
    }
  });
  const sortedYears = Object.entries(yearCounts).sort((a, b) => a[0].localeCompare(b[0]));
  const maxYearCount = Math.max(...sortedYears.map(y => y[1]), 1);

  // Build donut chart SVG
  const buildDonut = () => {
    if (sortedComponents.length === 0) return null;
    const cx = 100, cy = 100, r = 70, r2 = 45;
    let cumAngle = -90;
    const slices = sortedComponents.slice(0, 10).map((comp, i) => {
      const pct = comp[1] / total;
      const startAngle = cumAngle;
      const endAngle = cumAngle + pct * 360;
      cumAngle = endAngle;

      const s1 = Math.PI * startAngle / 180;
      const e1 = Math.PI * endAngle / 180;
      const large = pct > 0.5 ? 1 : 0;

      const x1o = cx + r * Math.cos(s1), y1o = cy + r * Math.sin(s1);
      const x2o = cx + r * Math.cos(e1), y2o = cy + r * Math.sin(e1);
      const x1i = cx + r2 * Math.cos(e1), y1i = cy + r2 * Math.sin(e1);
      const x2i = cx + r2 * Math.cos(s1), y2i = cy + r2 * Math.sin(s1);

      const d = `M ${x1o} ${y1o} A ${r} ${r} 0 ${large} 1 ${x2o} ${y2o} L ${x1i} ${y1i} A ${r2} ${r2} 0 ${large} 0 ${x2i} ${y2i} Z`;

      return <path key={i} d={d} fill={COLORS[i % COLORS.length]} opacity="0.85"/>;
    });

    return (
      <svg viewBox="0 0 200 200" width="180" height="180">
        {slices}
        <text x="100" y="96" textAnchor="middle" fontSize="22" fontWeight="700" fill="#0f172a">{total}</text>
        <text x="100" y="114" textAnchor="middle" fontSize="10" fill="#64748b">complaints</text>
      </svg>
    );
  };

  return (
    <div>
      {/* AI Insights */}
      {history.length > 0 && (
        <div style={{background:'linear-gradient(135deg,#1e293b,#334155)',borderRadius:'12px',padding:'20px',color:'#fff',marginBottom:'20px'}}>
          <h3 style={{margin:'0 0 12px',fontSize:'15px'}}>🤖 AI-Powered Reliability Insights</h3>
          <div style={{fontSize:'13px',lineHeight:'1.7',opacity:'.9'}}>
            <p style={{margin:'0 0 8px'}}>
              <strong>Analysis Summary:</strong> This vehicle shows {history.length} complaint{history.length !== 1 ? 's' : ''} with
              an estimated failure rate of {vehicle.predictive_risk ? vehicle.predictive_risk.risk_1yr : 'N/A'}%.
              {sortedComponents.length > 0 && ` The primary issue is ${sortedComponents[0][0]} (${sortedComponents[0][1]} incident${sortedComponents[0][1] !== 1 ? 's' : ''}), accounting for ${Math.round(sortedComponents[0][1] / total * 100)}% of all complaints.`}
            </p>
            <p style={{margin:'0 0 4px'}}>
              <strong>Safety Concerns:</strong> {history.filter(h => h.crash || h.fire).length} safety-related incidents detected.
            </p>
            <p style={{margin:'0'}}>
              <strong>Comparative Risk:</strong> {vehicle.comparative && vehicle.comparative.vehicle_complaints < vehicle.comparative.make_avg ? 'Better' : 'Worse'} than make average.
              <br/>
              <strong>Recommendation:</strong> {vehicle.reliability_score >= 80 ? '✅ Low risk — standard maintenance recommended.' : vehicle.reliability_score >= 50 ? '⚠️ Moderate risk — enhanced monitoring recommended.' : '🔴 High risk — detailed inspection recommended.'}
            </p>
          </div>
        </div>
      )}

      {history.length >= 3 && <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'20px',marginBottom:'20px'}}>
        {/* Complaints Over Time */}
        <div style={{background:'#fff',borderRadius:'12px',padding:'20px',boxShadow:'0 1px 3px rgba(0,0,0,.08)'}}>
          <h3 style={{margin:'0 0 16px',fontSize:'15px'}}>📈 Complaints Over Time</h3>
          {sortedYears.length > 0 ? (
            <svg viewBox={`0 0 ${Math.max(300, sortedYears.length * 40)} 160`} width="100%" height="160" style={{overflow:'visible'}}>
              {/* Grid lines */}
              {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
                <line key={i} x1="30" y1={130 - pct * 110} x2={sortedYears.length * 40 + 10} y2={130 - pct * 110} stroke="#f1f5f9" strokeWidth="1"/>
              ))}
              {/* Area */}
              <path
                d={`M 40 130 ${sortedYears.map((y, i) => `L ${40 + i * 40} ${130 - (y[1] / maxYearCount) * 110}`).join(' ')} L ${40 + (sortedYears.length - 1) * 40} 130 Z`}
                fill="url(#areaGrad)" opacity="0.3"
              />
              {/* Line */}
              <polyline
                points={sortedYears.map((y, i) => `${40 + i * 40},${130 - (y[1] / maxYearCount) * 110}`).join(' ')}
                fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinejoin="round"
              />
              {/* Dots */}
              {sortedYears.map((y, i) => (
                <g key={i}>
                  <circle cx={40 + i * 40} cy={130 - (y[1] / maxYearCount) * 110} r="4" fill="#6366f1"/>
                  <text x={40 + i * 40} y="148" textAnchor="middle" fontSize="8" fill="#94a3b8">{y[0]}</text>
                  <text x={40 + i * 40} y={125 - (y[1] / maxYearCount) * 110} textAnchor="middle" fontSize="8" fill="#6366f1" fontWeight="600">{y[1]}</text>
                </g>
              ))}
              <defs>
                <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity="0.4"/>
                  <stop offset="100%" stopColor="#6366f1" stopOpacity="0.05"/>
                </linearGradient>
              </defs>
            </svg>
          ) : <div style={{color:'#94a3b8',fontSize:'13px'}}>No timeline data available</div>}
        </div>

        {/* Component Breakdown Donut */}
        <div style={{background:'#fff',borderRadius:'12px',padding:'20px',boxShadow:'0 1px 3px rgba(0,0,0,.08)'}}>
          <h3 style={{margin:'0 0 16px',fontSize:'15px'}}>🍩 Component Breakdown</h3>
          <div style={{display:'flex',gap:'16px',alignItems:'flex-start'}}>
            <div style={{flexShrink:0}}>
              {buildDonut()}
            </div>
            <div style={{fontSize:'11px',lineHeight:'2'}}>
              {sortedComponents.slice(0, 8).map((comp, i) => (
                <div key={i} style={{display:'flex',alignItems:'center',gap:'6px'}}>
                  <span style={{width:'10px',height:'10px',borderRadius:'2px',background:COLORS[i % COLORS.length],flexShrink:0}}/>
                  <span style={{color:'#475569'}}>{comp[0]} ({comp[1]})</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>}

      {/* Complaint History */}
      {history.length > 0 && (
        <div style={{background:'#fff',borderRadius:'12px',padding:'20px',boxShadow:'0 1px 3px rgba(0,0,0,.08)'}}>
          <h3 style={{margin:'0 0 16px',fontSize:'15px'}}>📋 Longitudinal Complaint History</h3>
          <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
            {history.slice(0, 20).map((h, i) => {
              const hasSafety = h.crash || h.fire;
              return (
                <div key={i} style={{
                  padding:'14px 18px',
                  background: hasSafety ? '#fef2f2' : '#f8fafc',
                  borderLeft: `4px solid ${hasSafety ? '#ef4444' : h.numberOfInjuries > 0 ? '#f59e0b' : '#6366f1'}`,
                  borderRadius:'0 8px 8px 0'
                }}>
                  <div style={{fontSize:'12px',color:'#64748b',marginBottom:'4px',fontWeight:'500'}}>
                    {h.dateOfIncident || h.dateComplaintFiled || 'Unknown date'}
                  </div>
                  <div style={{fontSize:'14px',fontWeight:'700',color:'#0f172a',marginBottom:'6px'}}>
                    {h.components || 'Unknown'}
                  </div>
                  <div style={{fontSize:'13px',color:'#475569',lineHeight:'1.6',maxHeight:'80px',overflow:'hidden'}}>
                    {h.summary || 'No details'}
                  </div>
                  {(h.crash || h.fire || h.numberOfInjuries > 0 || h.numberOfDeaths > 0) && (
                    <div style={{display:'flex',gap:'6px',marginTop:'8px'}}>
                      {h.crash && <span style={{fontSize:'11px',fontWeight:'600',padding:'2px 8px',borderRadius:'4px',background:'#fecaca',color:'#991b1b'}}>CRASH</span>}
                      {h.fire && <span style={{fontSize:'11px',fontWeight:'600',padding:'2px 8px',borderRadius:'4px',background:'#fed7aa',color:'#9a3412'}}>FIRE</span>}
                      {h.numberOfInjuries > 0 && <span style={{fontSize:'11px',fontWeight:'600',padding:'2px 8px',borderRadius:'4px',background:'#fde68a',color:'#854d0e'}}>{h.numberOfInjuries} INJURED</span>}
                      {h.numberOfDeaths > 0 && <span style={{fontSize:'11px',fontWeight:'600',padding:'2px 8px',borderRadius:'4px',background:'#1e293b',color:'#fff'}}>{h.numberOfDeaths} DEATHS</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default ChartsComponent;
